import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Timer,
  PlayCircleOutline,
  WorkspacePremium,
  Monitor,
  ViewInAr,
  LocalGasStation,
  Factory,
  Computer,
  HealthAndSafety,
  Troubleshoot,
  KeyboardArrowDown,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import { getNextWorkshopDate } from '../services/api.js';
import { useAuth } from '../context/AuthContext.js';
import ReatorBg from '../assets/bg-landing.png';
import TrailerBg from '../assets/background_trailer.png';
import DominarBg from '../assets/dominar.png';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userRole, isBootstrapping } = useAuth();
  const [workshopDate, setWorkshopDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(false);

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  useEffect(() => {
    if (isBootstrapping || !isAuthenticated) return;

    if (userRole === 'MBR' || !userRole) {
      navigate('/cursos', { replace: true });
    } else if (userRole === 'ADM') {
      navigate('/dashboard', { replace: true });
    } else if (userRole === 'COR' || userRole === 'MON') {
      navigate('/users', { replace: true });
    }
  }, [navigate, isAuthenticated, userRole, isBootstrapping]);

  useEffect(() => {
    const fetchWorkshopDate = async () => {
      try {
        setLoading(true);
        const data = await getNextWorkshopDate();
        setWorkshopDate(data.date);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkshopDate();
  }, []);

  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className='min-h-screen font-sans text-white overflow-x-hidden bg-[#0f2c3a]'>
      {/* HERO SECTION */}
      <section className='relative h-screen flex flex-col justify-center'>
        <div
          className='absolute inset-0 z-0'
          style={{
            backgroundImage: `url(${ReatorBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className='absolute inset-0 bg-[#0f2c3a]/60 mix-blend-multiply' />
          <div className='absolute inset-0 bg-gradient-to-t from-[#0f2c3a] via-transparent to-transparent' />
        </div>

        <div className='relative z-10 container mx-auto px-6 h-full flex flex-col justify-center max-w-[1536px] pt-20'>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className='max-w-3xl'
          >
            <p className='text-3xl md:text-4xl font-light mb-2'>
              Domine a Operação de Reatores
            </p>
            <h1 className='text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-none'>
              <span className='text-transparent bg-clip-text bg-gradient-to-r from-[#8a4fff] to-[#00f0ff]'>
                FISCHER-TROPSCH
              </span>
            </h1>
            <p className='text-base md:text-lg tracking-[0.2em] font-light uppercase mb-12 text-gray-300'>
              PREPARE-SE PARA O FUTURO DA ENERGIA, HOJE.
            </p>

            <p className='text-lg md:text-xl font-light leading-relaxed max-w-2xl mb-12 text-gray-200'>
              A capacitação imersiva que{' '}
              <strong className='font-bold text-white'>
                transforma seu conhecimento em performance
              </strong>{' '}
              e <strong className='font-bold text-white'>segurança</strong> na
              produção de Combustível Sustentável de Aviação (SAF) a partir do
              Biogás.
            </p>

            {/* <div className='flex flex-col md:flex-row items-center gap-8 mb-12'>
              {loading ? (
                <div className='text-cyan-400 text-base animate-pulse'>
                  Carregando data...
                </div>
              ) : error ? (
                <div className='text-red-400 text-base'>Indisponível</div>
              ) : workshopDate ? (
                <div className='text-left'>
                  <p className='text-cyan-400 text-base uppercase tracking-wider mb-1'>
                    Próxima Turma
                  </p>
                  <p className='text-2xl font-bold'>
                    {formatDate(workshopDate)}
                  </p>
                </div>
              ) : null}
            </div> */}

            <Link to='/signup'>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className='bg-[#a8d35e] hover:bg-[#97be53] text-[#0f2c3a] font-bold py-4 px-8 rounded-full shadow-lg text-sm md:text-base tracking-wide transition-colors'
              >
                QUERO GARANTIR MINHA VAGA
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* YELLOW/GREEN BAR FEATURES */}
      <section className='relative z-20 bg-gradient-to-r from-[#a8d35e] to-[#d6ef7e] text-[#0f2c3a] py-8 shadow-xl'>
        <div className='container mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-center max-w-[1536px]'>
          <div>
            <h3 className='font-extrabold text-2xl flex items-center justify-center mb-2'>
              <span className='mr-2'>+</span> SEGURANÇA
            </h3>
            <p className='text-base leading-tight font-medium opacity-90 max-w-xs mx-auto'>
              Treine procedimentos e respostas a falhas em um ambiente 100%
              seguro e controlado.
            </p>
          </div>
          <div>
            <h3 className='font-extrabold text-2xl flex items-center justify-center mb-2'>
              <span className='mr-2'>+</span> EFICIÊNCIA
            </h3>
            <p className='text-base leading-tight font-medium opacity-90 max-w-xs mx-auto'>
              Profissionais preparados levam a uma gestão otimizada e menos
              paradas operacionais.
            </p>
          </div>
          <div>
            <h3 className='font-extrabold text-2xl flex items-center justify-center mb-2'>
              <span className='mr-2'>+</span> APRENDIZADO
            </h3>
            <p className='text-base leading-tight font-medium opacity-90 max-w-xs mx-auto'>
              A imersão 3D aliada à prática acelera o aprendizado com mais
              eficácia que métodos tradicionais.
            </p>
          </div>
        </div>
      </section>

      {/* CURSO CONTENT SECTION */}
      <section className='relative w-full overflow-hidden'>
        {/* Background image — engineer + plant on the left */}
        <div
          className='absolute inset-0'
          style={{
            backgroundImage: `url(${DominarBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center left',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Gradient overlay — lighter on left, darker on right for text contrast */}
        <div
          className='absolute inset-0'
          style={{
            background:
              'linear-gradient(to right, rgba(16, 42, 48, 0.15) 0%, rgba(16, 42, 48, 0.55) 45%, rgba(16, 42, 48, 0.88) 70%, rgba(16, 42, 48, 0.95) 100%)',
          }}
        />

        <div className='relative z-10 container mx-auto px-6 max-w-[1536px] py-14 md:py-20 lg:py-24'>
          <h2 className='text-center mb-10 md:mb-14 font-bold text-white' style={{ fontSize: 'clamp(28px, 3.5vw, 52px)' }}>
            O que você vai <span className='text-[#a8d35e]'>dominar</span> neste{' '}
            <span className='text-[#a8d35e]'>curso</span>?
          </h2>

          <div className='flex flex-col lg:flex-row lg:items-stretch'>
            {/* Left column — tagline positioned at bottom on desktop */}
            <div className='hidden lg:flex lg:flex-col lg:justify-end lg:w-[32%] shrink-0 pr-6 pb-2'>
              <p className='text-[20px] sm:text-xl uppercase text-center tracking-[0.15em] text-[#5a7a82] font-medium max-w-sm'>
                O mercado de SAF avança. Você ou sua equipe estão preparados?
              </p>
            </div>

            {/* Module blocks — right column */}
            <div className='w-full lg:w-[68%] space-y-4 md:space-y-5 max-w-2xl lg:max-w-none lg:ml-auto'>
              {[
                {
                  icon: (
                    <LocalGasStation className='text-white' sx={{ fontSize: 'clamp(28px, 2.5vw, 40px)' }} />
                  ),
                  title: 'Fundamentos de SAF e Plantas PBtL',
                  desc: 'Entenda o contexto da produção de combustíveis sustentáveis a partir de biogás e resíduos.',
                },
                {
                  icon: <Factory className='text-white' sx={{ fontSize: 'clamp(28px, 2.5vw, 40px)' }} />,
                  title: 'Processo Fischer-Tropsch',
                  desc: 'Aprofunde-se na teoria e nas variáveis críticas do processo.',
                },
                {
                  icon: <Computer className='text-white' sx={{ fontSize: 'clamp(28px, 2.5vw, 40px)' }} />,
                  title: 'Operação e Manutenção do Reator',
                  desc: 'Aprenda e pratique os procedimentos de partida, parada e operação do reator no Simulador 3D.',
                },
                {
                  icon: (
                    <HealthAndSafety className='text-white' sx={{ fontSize: 'clamp(28px, 2.5vw, 40px)' }} />
                  ),
                  title: 'Normas e Rotinas de Segurança',
                  desc: 'Domine os protocolos de segurança indispensáveis para a operação.',
                },
                {
                  icon: (
                    <Troubleshoot className='text-white' sx={{ fontSize: 'clamp(28px, 2.5vw, 40px)' }} />
                  ),
                  title: 'Diagnóstico e Respostas a Falhas',
                  desc: 'Treine a tomada de decisão em cenários de falha para garantir a estabilidade do processo.',
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className='flex items-start rounded-xl border border-white/10'
                  style={{
                    gap: 'clamp(16px, 1.5vw, 24px)',
                    padding: 'clamp(18px, 2vw, 32px) clamp(20px, 2.5vw, 36px)',
                    background: 'rgba(16, 42, 48, 0.35)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <div className='shrink-0 mt-1'>
                    {item.icon}
                  </div>
                  <div className='min-w-0'>
                    <h4
                      className='text-[#a8d35e] font-bold leading-snug'
                      style={{ fontSize: 'clamp(16px, 1.3vw, 22px)', marginBottom: 'clamp(4px, 0.4vw, 8px)' }}
                    >
                      {item.title}
                    </h4>
                    <p
                      className='text-white/90 leading-relaxed'
                      style={{ fontSize: 'clamp(14px, 1.1vw, 18px)' }}
                    >
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tagline on mobile (hidden on lg) */}
          <p className='lg:hidden mt-10 md:mt-14 text-[20px] sm:text-xl uppercase text-center tracking-[0.15em] text-[#5a7a82] font-medium max-w-sm mx-auto'>
            O mercado de SAF avança. Você ou sua equipe estão preparados?
          </p>
        </div>
      </section>

      {/* MODALITIES */}
      <section className='py-20 bg-white text-[#0f2c3a]'>
        <div className='container mx-auto px-6 max-w-[1536px] text-center'>
          <h2 className='text-4xl font-bold mb-16'>
            Descubra a <span className='text-[#8a4fff] italic'>modalidade</span>
            <br />
            <span className='text-[#00f0ff] italic'>ideal</span> para você
          </h2>

          <div className='grid md:grid-cols-2 gap-8'>
            {/* Remota */}
            <div className='bg-gray-50 p-8 rounded-2xl shadow-lg text-left hover:shadow-xl transition-shadow border border-gray-100 flex flex-col'>
              {/* Placeholder for Image */}
              {/* <div className='h-40 bg-gray-200 rounded-lg mb-6 flex items-center justify-center text-gray-400'>
                [Imagem Remota]
              </div> */}
              <div className='flex justify-start'>
                <span className='bg-[#00f0ff] text-[#0f2c3a] text-sm font-bold px-3 py-1 rounded-full uppercase'>
                  Flexibilidade
                </span>
              </div>
              <h3 className='text-2xl font-bold mt-4 mb-2'>Remota</h3>
              <p className='text-gray-600 text-base mb-6 flex-grow'>
                Acesso total ao conteúdo teórico, vídeos das aulas práticas e ao
                simulador 3D para você treinar a operação no seu próprio ritmo e
                de qualquer lugar.
              </p>
              <div className='mt-auto'>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setExpandedFaq(5);
                    const faqEl = document.getElementById('faq');
                    if (faqEl) {
                      const y = faqEl.getBoundingClientRect().top + window.scrollY - 100;
                      window.scrollTo({ top: y, behavior: 'smooth' });
                    }
                  }}
                  className='text-[#00f0ff] font-bold text-base border-b-2 border-[#00f0ff] hover:opacity-80'
                >
                  Saiba Mais
                </button>
              </div>
            </div>

            {/* Híbrida */}
            <div className='bg-gray-50 p-8 rounded-2xl shadow-lg text-left hover:shadow-xl transition-shadow border border-gray-100 flex flex-col'>
              {/* Placeholder for Image */}
              {/* <div className='h-40 bg-gray-200 rounded-lg mb-6 flex items-center justify-center text-gray-400'>
                [Imagem Híbrida]
              </div> */}
              <div className='flex justify-start'>
                <span className='bg-[#ffb703] text-[#0f2c3a] text-sm font-bold px-3 py-1 rounded-full uppercase'>
                  Imersão
                </span>
              </div>
              <h3 className='text-2xl font-bold mt-4 mb-2'>Híbrida</h3>
              <p className='text-gray-600 text-base mb-6 flex-grow'>
                Além do acesso online, participe de uma semana presencial com
                instrutores para operar uma planta real em escala piloto e
                trocar experiências.
              </p>
              <div className='mt-auto'>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setExpandedFaq(5);
                    const faqEl = document.getElementById('faq');
                    if (faqEl) {
                      const y = faqEl.getBoundingClientRect().top + window.scrollY - 100;
                      window.scrollTo({ top: y, behavior: 'smooth' });
                    }
                  }}
                  className='text-[#ffb703] font-bold text-base border-b-2 border-[#ffb703] hover:opacity-80'
                >
                  Saiba Mais
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMMERSION VIDEO SECTION */}
      <section className='w-full bg-white'>
        <div className='relative overflow-hidden w-full min-h-[1300px] md:min-h-[1200px] bg-white'>
          {/* White base — shows through transparent areas of the PNG */}
          <div className='absolute inset-0 bg-white' />

          {/* Background image — plant + engineer visible at top/right */}
          <div
            className='absolute inset-0'
            style={{
              backgroundImage: `url(${TrailerBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              backgroundRepeat: 'no-repeat',
            }}
          />

          {/* Gradient: only from mid-section down, keeps top/image area clear */}
          <div
            className='absolute inset-0'
            style={{
              background:
                'linear-gradient(to bottom, transparent 0%, transparent 35%, rgba(10, 46, 54, 0.45) 55%, rgba(10, 46, 54, 0.85) 75%, rgba(10, 46, 54, 0.9) 100%)',
            }}
          />

          <div className='relative z-10 container mx-auto px-6 max-w-[1536px] pt-8 md:pt-12 pb-8 md:pb-12'>
            <h2 className='text-left mt-24 sm:mt-32 md:mt-30 pt-30 lg:mt-48 mb-8 md:mb-12 drop-shadow-md'>
              <span className='block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-white leading-tight'>
                Conheça
              </span>
              <span className='block text-white leading-none mt-1'>
                <span className='text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light'>
                  a{' '}
                </span>
                <span className='text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black'>
                  Imersão
                </span>
              </span>
            </h2>

            {/* Video player */}
            <div className='w-full max-w-4xl lg:max-w-5xl mx-auto mb-8 md:mb-10'>
              <div className='relative rounded-xl md:rounded-2xl overflow-hidden shadow-2xl border border-white/20 group cursor-pointer'>
                <div
                  className='aspect-video flex items-center justify-center'
                  style={{
                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.55)), url(${TrailerBg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <PlayCircleOutline
                    style={{ fontSize: 'clamp(56px, 12vw, 90px)' }}
                    className='text-white opacity-90 group-hover:opacity-100 transition-opacity drop-shadow-lg'
                  />
                </div>
              </div>
            </div>

            {/* Course info cards */}
            <div className='w-full max-w-7xl mx-auto'>
              <p className='text-center text-xs sm:text-sm uppercase tracking-[0.25em] md:tracking-[0.3em] text-white font-bold mb-6 md:mb-8'>
                Informações do Curso
              </p>

              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5'>
                {[
                  {
                    icon: <Timer sx={{ fontSize: 'clamp(26px, 2.2vw, 34px)' }} className='text-[#a8d35e]' />,
                    label: 'Duração',
                    value: '40 horas',
                  },
                  {
                    icon: <WorkspacePremium sx={{ fontSize: 'clamp(26px, 2.2vw, 34px)' }} className='text-[#a8d35e]' />,
                    label: 'Certificado',
                    value: 'Emissão imediata',
                  },
                  {
                    icon: <Monitor sx={{ fontSize: 'clamp(26px, 2.2vw, 34px)' }} className='text-[#a8d35e]' />,
                    label: 'Ambiente',
                    value: 'Específico',
                  },
                  {
                    icon: <ViewInAr sx={{ fontSize: 'clamp(26px, 2.2vw, 34px)' }} className='text-[#a8d35e]' />,
                    label: 'Atividades',
                    value: 'Ambiente 3D',
                  },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className='flex items-center rounded-xl border border-[#a8d35e]/30 shadow-lg'
                    style={{
                      gap: 'clamp(12px, 1.2vw, 18px)',
                      padding: 'clamp(14px, 1.5vw, 24px) clamp(16px, 1.8vw, 28px)',
                      background: 'rgba(16, 42, 48, 0.35)',
                      backdropFilter: 'blur(12px)',
                      WebkitBackdropFilter: 'blur(12px)',
                    }}
                  >
                    <div
                      className='shrink-0'
                      style={{
                        width: 'clamp(56px, 4.5vw, 72px)',
                        height: 'clamp(44px, 3.5vw, 56px)',
                        borderRadius: '12px',
                        background: 'rgba(168, 211, 94, 0.12)',
                        border: '1px solid rgba(168, 211, 94, 0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </div>
                    <div className='min-w-0'>
                      <p
                        className='font-bold text-white leading-tight'
                        style={{ fontSize: 'clamp(14px, 1.1vw, 18px)' }}
                      >
                        {item.label}
                      </p>
                      <p
                        className='text-gray-300 leading-snug mt-0.5'
                        style={{ fontSize: 'clamp(14px, 1.1vw, 18px)' }}
                      >
                        {item.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TARGET AUDIENCE - "Para quem?" */}
      <section className='py-20 bg-gray-50 text-[#0f2c3a]'>
        <div className='container mx-auto px-6 max-w-[1536px] flex flex-col md:flex-row items-center gap-12'>
          <div className='md:w-1/3 text-right md:text-right'>
            <h2 className='text-4xl font-bold mb-4'>
              Para <br /> quem?
            </h2>
            <p className='text-base text-gray-600'>
              Este curso é ideal para quem busca se destacar no setor de
              energias renováveis e processos químicos.
            </p>
          </div>

          <div className='md:w-2/3 bg-white p-8 rounded-2xl shadow-sm border border-gray-100'>
            <ul className='space-y-4'>
              {[
                'Engenheiros Químicos e de Processos',
                'Técnicos de Operação e Manutenção',
                'Pesquisadores em Combustíveis Sustentáveis',
                'Profissionais da área de Segurança do Trabalho',
                'Estudantes que buscam diferencial competitivo',
              ].map((item, idx) => (
                <li key={idx} className='flex items-center gap-3'>
                  <div className='h-2 w-2 rounded-full bg-[#a8d35e]' />
                  <span className='font-medium'>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ABOUT US - "Quem Somos" */}
      <section className='py-20 bg-gray-100 text-[#0f2c3a]'>
        <div className='container mx-auto px-6 max-w-[1536px]'>
          <div className='bg-white p-10 rounded-3xl shadow-lg flex flex-col md:flex-row items-center gap-10'>
            <div className='flex-1 order-2 md:order-1'>
              <p className='text-base leading-relaxed text-gray-600 text-justify'>
                A necessidade de capacitação técnica em reatores de Fischer
                Tropsch (FT) é crucial para a operação eficiente e segura desses
                equipamentos em uma planta de produção de SAF. A qualificação de
                mão de obra de profissionais ligados à operação de reatores é
                escassa, especialmente no Brasil. Neste sentido, o treinamento
                imersivo que combina teoria e prática, utilizando tecnologias
                avançadas como modelagem 3D e realidade virtual, é essencial
                para preparar operadores e técnicos, reduzindo riscos
                operacionais e aumentando a eficiência dos processos.
              </p>
            </div>
            <div className='md:w-1/3 flex flex-col justify-center items-start border-l-4 border-gray-200 pl-6 order-1 md:order-2'>
              <h2 className='text-3xl font-black mb-1'>Sobre o</h2>
              <h2 className='text-3xl font-black'>Projeto</h2>
            </div>
          </div>
        </div>
      </section>

      {/* DÚVIDAS FREQUENTES */}
      <section id='faq' className='py-20 bg-white text-[#0f2c3a]'>
        <div className='container mx-auto px-6 max-w-[1536px]'>
          <h2 className='text-3xl md:text-4xl font-bold text-center mb-12'>
            Dúvidas frequentes
          </h2>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #EAEAEA',
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          >
            {[
              {
                question: 'Como faço meu cadastro na plataforma?',
                answer:
                  'Clique em “Cadastre-se” ou “Quero garantir minha vaga”, preencha os dados solicitados e confirme o cadastro através do e-mail que você receberá. Depois, acesse sua conta para consultar as turmas disponíveis e selecionar aquela da qual deseja participar.',
              },
              {
                question: 'Como faço meu primeiro acesso?',
                answer:
                  'Após concluir o cadastro, utilize o e-mail e a senha definidos para entrar na plataforma. Caso tenha sido inscrito por uma empresa parceira, siga as orientações enviadas para o seu e-mail corporativo.',
              },
              {
                question: 'Não recebi o e-mail de acesso. O que devo fazer?',
                answer:
                  'Verifique as pastas de spam, lixo eletrônico e promoções. Confirme também se o endereço informado no cadastro está correto. Caso o e-mail não seja localizado, solicite um novo envio ou entre em contato com o suporte.',
              },
              {
                question: 'Como redefino minha senha?',
                answer:
                  'Na página de Login, clique em "Esqueci minha senha", informe o e-mail cadastrado e siga as instruções recebidas para criar uma nova senha.',
              },
              {
                question:
                  'O que é o Ambiente Virtual 3D e preciso instalar algum programa?',
                answer:
                  'O Ambiente Virtual 3D é o simulador imersivo utilizado nas atividades práticas do curso. Nele, você poderá navegar pela planta, reconhecer equipamentos e executar procedimentos de operação, segurança e manutenção.\nO acesso foi planejado para ocorrer remotamente por streaming, sem exigir um computador de alto desempenho. Não é necessária a instalação do simulador no dispositivo do participante.',
              },
              {
                question:
                  'Qual é a diferença entre as modalidades remota e híbrida?',
                answer:
                  'Na modalidade remota, os conteúdos, videoaulas, materiais, avaliações e atividades no simulador são realizados online, com flexibilidade de horário dentro do período da turma.\nNa modalidade híbrida, parte da capacitação também acontece online, mas existem etapas síncronas ou presenciais em datas previamente definidas. A participação nessas etapas depende do cumprimento dos pré-requisitos estabelecidos para a turma.',
              },
            ].map((faq, index) => (
              <Accordion
                key={index}
                expanded={expandedFaq === index}
                onChange={handleFaqChange(index)}
                elevation={0}
                disableGutters
                square
                sx={{
                  borderTop:
                    index === 0 ? 'none' : '1px solid #EAEAEA',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<KeyboardArrowDown />}
                  sx={{
                    '& .MuiAccordionSummary-content': { my: 2 },
                    bgcolor: '#fff',
                  }}
                >
                  <Typography fontWeight='700' sx={{ color: '#0A2540' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    bgcolor: '#F8F9FA',
                    p: 3,
                    borderTop: '1px solid #EAEAEA',
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      color: '#4A5568',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
